/**
 * O que faz: Cria uma versão alternativa da landing pública com copy mais direta e animações inspiradas em scrollytelling.
 * Onde usa: Rota pública /landing-v2 para testar uma nova abordagem sem alterar a landing atual.
 * Conexões: Usa React Router para CTAs, Helmet para SEO, componentes de UI locais e CSS isolado em landing-v2.css.
 * Tabelas: Não interage com Supabase ou qualquer tabela; todo o conteúdo é estático para reduzir risco em página pública.
 * Data: 29/04/2026
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Activity,
  ArrowRight,
  BarChart3,
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  Clock,
  FileText,
  KanbanSquare,
  MessageCircle,
  Sparkles,
  TrendingUp,
  XCircle,
  Zap,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import "@/styles/landing-v2.css";
import { SimboloGens } from "@/components/brand/MarcaGens";

type RevealProps = {
  children: React.ReactNode;
  className?: string;
  delay?: number;
};

type ProgressStep = {
  id: string;
  label: string;
};

const progressSteps: ProgressStep[] = [
  { id: "atendimento", label: "Atendimento" },
  { id: "organizacao", label: "Organização" },
  { id: "crescimento", label: "Crescimento" },
];

const resourceChips = [
  "WhatsApp com IA",
  "Pipeline Kanban",
  "Agenda inteligente",
  "Follow-up automático",
  "Relatórios de IA",
  "Prontuário digital",
  "Leads de anúncios",
  "Mensagens programadas",
];

const costs = [
  {
    value: "15 min",
    label: "é o tempo em que um lead quente pode esfriar se ninguém responde",
  },
  {
    value: "3 canais",
    label: "costumam dividir a operação: WhatsApp, agenda e planilhas",
  },
  {
    value: "1 visão",
    label: "é o que a clínica precisa para saber onde vender mais",
  },
];

const benefits = [
  {
    icon: MessageCircle,
    title: "Atendimento que responde rápido",
    text: "A IA ajuda a responder dúvidas comuns, qualificar o lead e manter a conversa andando mesmo fora do horário comercial.",
  },
  {
    icon: KanbanSquare,
    title: "Funil visual para não perder pacientes",
    text: "Cada lead fica em uma etapa clara, da primeira mensagem ao agendamento, com histórico e próximos passos visíveis.",
  },
  {
    icon: CalendarCheck,
    title: "Agenda conectada à rotina da clínica",
    text: "A equipe acompanha compromissos, confirmações e retornos sem depender de anotações soltas.",
  },
  {
    icon: BarChart3,
    title: "Relatórios que mostram onde crescer",
    text: "O Gens transforma conversas, anúncios e atendimentos em insights simples para melhorar campanhas e conversão.",
  },
];

const journey = [
  {
    title: "Lead chega pelo WhatsApp ou anúncio",
    text: "O contato entra no sistema com origem, etapa e contexto para a equipe saber de onde veio.",
  },
  {
    title: "IA responde e qualifica",
    text: "Perguntas frequentes, serviços e objeções iniciais são tratados com base nas informações da clínica.",
  },
  {
    title: "Equipe agenda e acompanha",
    text: "O funil mostra quem precisa de resposta, quem está pronto para consulta e quem precisa de follow-up.",
  },
  {
    title: "Gestor enxerga o resultado",
    text: "Relatórios mostram tempo de resposta, conversões, gargalos e oportunidades de crescimento.",
  },
];

const plans = [
  {
    name: "Basic",
    price: "R$ 97/mês",
    description: "Para clínicas que querem organizar leads e começar com IA.",
    features: ["WhatsApp com IA básica", "Pipeline de leads", "2 relatórios de IA por mês", "Suporte por chat"],
  },
  {
    name: "Pro",
    price: "R$ 197/mês",
    description: "Para clínicas que já investem em anúncios e precisam vender melhor.",
    features: ["IA completa no atendimento", "Follow-up automático", "4 relatórios de IA por mês", "Importação e exportação de contatos"],
    featured: true,
  },
  {
    name: "Premium",
    price: "Sob consulta",
    description: "Para operação completa com prontuário, agenda e análise avançada.",
    features: ["Prontuário digital", "IA de agendamento", "Relatórios ilimitados", "Acompanhamento prioritário"],
  },
];

const faqs = [
  {
    question: "Preciso trocar o WhatsApp da clínica?",
    answer: "Não. A proposta é conectar o atendimento ao Gens CRM para organizar conversas, leads e etapas sem mudar a rotina principal da equipe.",
  },
  {
    question: "A IA atende sozinha?",
    answer: "Ela pode apoiar respostas e qualificação, mas a clínica define o nível de automação. A equipe continua no controle quando uma conversa exige cuidado humano.",
  },
  {
    question: "Quando o acesso é liberado?",
    answer: "Depois do cadastro, a clínica escolhe um plano e conclui o pagamento seguro pelo Stripe. O CRM é liberado assim que o primeiro pagamento for confirmado.",
  },
  {
    question: "Essa página usa dados reais do Supabase?",
    answer: "Não. Esta versão é uma landing estática. Nenhuma consulta, inserção, atualização ou exclusão é feita no banco de dados.",
  },
];

const imagePlaceholders = [
  "Imagem IA: dashboard do Gens CRM em notebook, com funil Kanban, agenda e gráficos limpos.",
  "Imagem IA: recepcionista de clínica acompanhando WhatsApp com IA em uma tela moderna.",
  "Imagem IA: gestor de clínica analisando relatório semanal de crescimento.",
];

const Reveal: React.FC<RevealProps> = ({ children, className = "", delay = 0 }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;

    // Evita executar animações quando o elemento ainda não existe no DOM.
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Revela uma única vez para manter a leitura fluida e evitar distração ao voltar o scroll.
        if (entry.isIntersecting) {
          window.setTimeout(() => setVisible(true), delay);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.18 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`landing-v2-reveal ${visible ? "is-visible" : ""} ${className}`}>
      {children}
    </div>
  );
};

const LandingPageV2: React.FC = () => {
  const [activeStep, setActiveStep] = useState(progressSteps[0].id);

  const doubledChips = useMemo(() => [...resourceChips, ...resourceChips], []);

  useEffect(() => {
    const sections = progressSteps
      .map((step) => document.getElementById(step.id))
      .filter((section): section is HTMLElement => Boolean(section));

    // Usa IntersectionObserver para atualizar o progresso sem medir scroll a cada pixel.
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.find((entry) => entry.isIntersecting);
        if (visibleEntry?.target.id) setActiveStep(visibleEntry.target.id);
      },
      {
        rootMargin: "-35% 0px -45% 0px",
        threshold: 0.01,
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-v2 min-h-screen bg-[#f8fbff] text-slate-950">
      <Helmet>
        <title>Gens CRM - Clínica organizada com IA, WhatsApp e funil de pacientes</title>
        <meta
          name="description"
          content="Organize WhatsApp, leads, agenda, follow-up e relatórios em uma plataforma com IA para clínicas. Escolha seu plano e comece agora."
        />
        <link rel="canonical" href={typeof window !== "undefined" ? `${window.location.origin}/landing-v2` : "/landing-v2"} />
      </Helmet>

      <header className="landing-v2-header">
        <Link to="/landing-v2" className="landing-v2-logo" aria-label="Ir para o início da landing v2">
          <span className="landing-v2-logo-mark" aria-hidden="true"><SimboloGens /></span>
          <span>Gens CRM</span>
        </Link>

        <nav className="landing-v2-nav" aria-label="Seções da landing">
          <a href="#atendimento">Atendimento</a>
          <a href="#organizacao">Organização</a>
          <a href="#crescimento">Crescimento</a>
          <a href="#planos">Planos</a>
          <a href="#faq">FAQ</a>
        </nav>

        <div className="landing-v2-header-actions">
          <Link to="/login" className="hidden text-sm font-semibold text-slate-700 hover:text-slate-950 sm:inline-flex">
            Entrar
          </Link>
          <Link to="/signup">
            <Button className="landing-v2-cta landing-v2-cta-small">Escolher plano</Button>
          </Link>
        </div>
      </header>

      <aside className="landing-v2-side-progress" aria-label="Progresso das seções principais">
        {progressSteps.map((step, index) => (
          <a
            key={step.id}
            href={`#${step.id}`}
            className={activeStep === step.id ? "is-active" : ""}
            aria-current={activeStep === step.id ? "step" : undefined}
          >
            <span>{String(index + 1).padStart(2, "0")}</span>
            {step.label}
          </a>
        ))}
      </aside>

      <main>
        <section className="landing-v2-hero" id="inicio">
          <div className="landing-v2-background-grid" aria-hidden="true" />
          <div className="landing-v2-ambient-line landing-v2-ambient-line-one" aria-hidden="true" />
          <div className="landing-v2-ambient-line landing-v2-ambient-line-two" aria-hidden="true" />

          <div className="landing-v2-hero-content">
            <Reveal>
              <p className="landing-v2-eyebrow">CRM com IA para clínicas que vendem pelo WhatsApp</p>
              <h1>
                Organize seus leads uma vez. <span>Venda melhor todos os dias.</span>
              </h1>
              <p className="landing-v2-subtitle">
                O Gens CRM conecta atendimento, funil, agenda, follow-up e relatórios em uma operação simples para a equipe usar sem depender de planilhas.
              </p>
              <div className="landing-v2-hero-actions">
                <Link to="/signup">
                  <Button className="landing-v2-cta">
                    Criar conta e escolher plano
                    <ArrowRight size={18} />
                  </Button>
                </Link>
                <a href="#como-funciona" className="landing-v2-secondary-link">
                  Ver como funciona
                </a>
              </div>
              <div className="landing-v2-proof-row" aria-label="Resumo de benefícios">
                <span>Pagamento seguro pelo Stripe</span>
                <span>IA configurável</span>
                <span>Feito para clínicas</span>
              </div>
            </Reveal>
          </div>

          <Reveal className="landing-v2-hero-product" delay={140}>
            <div className="landing-v2-product-shell">
              <div className="landing-v2-product-topbar">
                <span />
                <span />
                <span />
                <strong>Gens CRM</strong>
              </div>

              <div className="landing-v2-product-layout">
                <div className="landing-v2-product-column">
                  <p className="landing-v2-product-label">Leads hoje</p>
                  <strong>32</strong>
                  <span className="landing-v2-product-growth">+18% vs. semana passada</span>
                </div>

                <div className="landing-v2-kanban-preview" aria-label="Prévia visual de funil Kanban">
                  {["Novo lead", "Em conversa", "Agendado"].map((column, columnIndex) => (
                    <div key={column} className="landing-v2-kanban-column">
                      <strong>{column}</strong>
                      {[0, 1].map((cardIndex) => (
                        <div key={`${column}-${cardIndex}`} className="landing-v2-kanban-card">
                          <span />
                          <p>{columnIndex === 0 ? "WhatsApp" : columnIndex === 1 ? "Retorno" : "Consulta"}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="landing-v2-floating-card landing-v2-floating-card-left">
                <MessageCircle size={18} />
                <span>IA respondeu em 38s</span>
              </div>
              <div className="landing-v2-floating-card landing-v2-floating-card-right">
                <TrendingUp size={18} />
                <span>Conversão subiu 22%</span>
              </div>
            </div>
          </Reveal>
        </section>

        <section className="landing-v2-marquee" aria-label="Recursos do Gens CRM">
          <div className="landing-v2-marquee-track">
            {doubledChips.map((chip, index) => (
              <span key={`${chip}-${index}`}>{chip}</span>
            ))}
          </div>
        </section>

        <section className="landing-v2-section" id="atendimento">
          <div className="landing-v2-section-grid">
            <Reveal>
              <p className="landing-v2-kicker">01 Atendimento</p>
              <h2>O problema não é só responder. É responder com contexto.</h2>
              <p>
                Quando o lead vem de anúncio, Instagram ou indicação, cada minuto sem resposta reduz a chance de agendar. O Gens centraliza a conversa para a equipe saber quem é o paciente, de onde veio e qual o próximo passo.
              </p>
            </Reveal>

            <Reveal delay={120}>
              <div className="landing-v2-cost-panel">
                {costs.map((item) => (
                  <div key={item.value}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="landing-v2-image-strip" aria-label="Sugestões de imagens para gerar com IA">
          {imagePlaceholders.map((placeholder, index) => (
            <Reveal key={placeholder} delay={index * 100}>
              <div className="landing-v2-image-placeholder">
                <Sparkles size={18} />
                <span>{placeholder}</span>
              </div>
            </Reveal>
          ))}
        </section>

        <section className="landing-v2-section landing-v2-section-contrast" id="organizacao">
          <Reveal className="landing-v2-centered">
            <p className="landing-v2-kicker">02 Organização</p>
            <h2>Troque operação espalhada por uma rotina visível.</h2>
            <p>
              A comparação precisa ser simples: de um lado, conversas perdidas e planilhas; do outro, funil, agenda e relatórios em uma plataforma única.
            </p>
          </Reveal>

          <div className="landing-v2-before-after">
            <Reveal>
              <div className="landing-v2-compare-panel is-before">
                <div className="landing-v2-panel-title">
                  <XCircle size={22} />
                  <strong>Antes do Gens</strong>
                </div>
                <ul>
                  <li>WhatsApp cheio e sem prioridade.</li>
                  <li>Leads em planilhas que ninguém atualiza.</li>
                  <li>Gestor sem clareza sobre origem e conversão.</li>
                  <li>Follow-up feito tarde demais.</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={140}>
              <div className="landing-v2-compare-panel is-after">
                <div className="landing-v2-panel-title">
                  <CheckCircle2 size={22} />
                  <strong>Com o Gens</strong>
                </div>
                <ul>
                  <li>Lead entra com origem, etapa e histórico.</li>
                  <li>IA apoia respostas e qualificação.</li>
                  <li>Agenda e funil mostram o próximo passo.</li>
                  <li>Relatórios apontam onde melhorar.</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="landing-v2-section" id="como-funciona">
          <Reveal className="landing-v2-centered">
            <p className="landing-v2-kicker">Como funciona</p>
            <h2>Uma jornada simples para transformar conversa em consulta.</h2>
          </Reveal>

          <div className="landing-v2-journey">
            {journey.map((item, index) => (
              <Reveal key={item.title} delay={index * 100}>
                <div className="landing-v2-journey-step">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="landing-v2-section landing-v2-section-benefits" id="crescimento">
          <Reveal className="landing-v2-centered">
            <p className="landing-v2-kicker">03 Crescimento</p>
            <h2>O sistema precisa mostrar o que fazer depois.</h2>
            <p>
              A clínica não precisa de mais uma ferramenta solta. Precisa de uma rotina para responder melhor, acompanhar leads e decidir com dados.
            </p>
          </Reveal>

          <div className="landing-v2-benefit-grid">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Reveal key={benefit.title} delay={index * 90}>
                  <article className="landing-v2-benefit-card">
                    <Icon size={28} />
                    <h3>{benefit.title}</h3>
                    <p>{benefit.text}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </section>

        <section className="landing-v2-section landing-v2-methodology">
          <Reveal>
            <p className="landing-v2-kicker">Por que funciona</p>
            <h2>Menos adivinhação. Mais rotina operacional.</h2>
          </Reveal>

          <div className="landing-v2-method-grid">
            <Reveal delay={80}>
              <div>
                <Activity size={24} />
                <h3>Velocidade de resposta</h3>
                <p>Ajuda a equipe a responder rápido porque o lead chega organizado e com contexto.</p>
              </div>
            </Reveal>
            <Reveal delay={160}>
              <div>
                <ClipboardList size={24} />
                <h3>Processo comercial claro</h3>
                <p>O funil mostra quem precisa de retorno, quem está pronto e quem ficou parado.</p>
              </div>
            </Reveal>
            <Reveal delay={240}>
              <div>
                <FileText size={24} />
                <h3>Relatório acionável</h3>
                <p>A IA resume gargalos e oportunidades para o gestor decidir sem abrir várias telas.</p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="landing-v2-section" id="planos">
          <Reveal className="landing-v2-centered">
            <p className="landing-v2-kicker">Planos</p>
            <h2>Comece com o plano que combina com a maturidade da sua clínica.</h2>
            <p>Todos os caminhos partem do mesmo princípio: testar sem fricção e evoluir quando a operação pedir mais automação.</p>
          </Reveal>

          <div className="landing-v2-pricing-grid">
            {plans.map((plan, index) => (
              <Reveal key={plan.name} delay={index * 100}>
                <article className={`landing-v2-price-card ${plan.featured ? "is-featured" : ""}`}>
                  {plan.featured && <span className="landing-v2-plan-badge">Mais escolhido</span>}
                  <h3>{plan.name}</h3>
                  <strong>{plan.price}</strong>
                  <p>{plan.description}</p>
                  <ul>
                    {plan.features.map((feature) => (
                      <li key={feature}>
                        <CheckCircle2 size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="landing-v2-final-cta">
          <Reveal>
            <Clock size={30} />
            <h2>Em vez de procurar lead perdido, veja a clínica inteira em uma tela.</h2>
            <p>Escolha o plano ideal e libere o CRM após a confirmação do primeiro pagamento.</p>
            <Link to="/signup">
              <Button className="landing-v2-cta">
                Criar conta e escolher plano
                <Zap size={18} />
              </Button>
            </Link>
          </Reveal>
        </section>

        <section className="landing-v2-section landing-v2-faq" id="faq">
          <Reveal className="landing-v2-centered">
            <p className="landing-v2-kicker">FAQ</p>
            <h2>Perguntas importantes antes de testar.</h2>
          </Reveal>

          <Reveal>
            <Accordion type="single" collapsible className="landing-v2-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem key={faq.question} value={`faq-${index}`}>
                  <AccordionTrigger>{faq.question}</AccordionTrigger>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </section>
      </main>

      <footer className="landing-v2-footer">
        <div>
          <Link to="/landing-v2" className="landing-v2-logo" aria-label="Gens CRM">
            <span className="landing-v2-logo-mark" aria-hidden="true"><SimboloGens /></span>
            <span>Gens CRM</span>
          </Link>
          <p>CRM com IA para clínicas que precisam organizar atendimento, leads, agenda e relatórios.</p>
        </div>

        <div>
          <h3>Produto</h3>
          <a href="#atendimento">Atendimento</a>
          <a href="#organizacao">Organização</a>
          <a href="#crescimento">Crescimento</a>
        </div>

        <div>
          <h3>Legal</h3>
          <Link to="/politica-de-privacidade">Política de Privacidade</Link>
          <span>CNPJ: 40.220.180/0001-31</span>
          <span>WhatsApp: (83) 99688-6297</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageV2;
